import { Code, DecryptedPatient } from '@icure/cardinal-sdk'
import { Button, Form, Popconfirm, Select } from 'antd'
import React, { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useCreateOrUpdatePatientMutation } from '../../../../../core/api/patientApi'
import { getPatientDataFormated } from '../../../../../helpers/patientDataManipulations'
import { CustomModal } from '../../../../common/CustomModal'
import './index.less'
import { SpinLoader } from '../../../../common/SpinLoader'
import { allPatientsTagsEnum, PatientsTagsEnum, PatientsTagsKeyByValue } from '../../../../../helpers/types'
import { useCreateOrUpdateCodesMutation, useGetCodesQuery } from '../../../../../core/api/codeApi'
import { getPreparedTagsToUpdatePatient } from './helpers'

type AddPatientTagsForm = {
  tags: PatientsTagsEnum[]
}

interface ModalAddPatientTagProps {
  isVisible: boolean
  onClose: () => void
  patient?: DecryptedPatient
}

export const ModalAddPatientTag = ({ isVisible, onClose, patient }: ModalAddPatientTagProps): ReactElement => {
  const [form] = Form.useForm()
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  const [
    createOrUpdatePatient,
    { error: createOrUpdatePatientError, isError: isCreateOrUpdatePatientError, isSuccess: isPatientCreatedOrUpdateSuccessfully, isLoading: isPatientCreatingOrUpdatingLoading },
  ] = useCreateOrUpdatePatientMutation()
  const [createTags] = useCreateOrUpdateCodesMutation()
  const { data: customCodes } = useGetCodesQuery('RETINO_BRIDGE')
  const [newTagsToCreate, setNewTagsToCreate] = React.useState<string[]>([])

  const defaultTagsTranslated = allPatientsTagsEnum.map((tag) => {
    const key = PatientsTagsKeyByValue[tag as string]
    return key ? t(`patients.tags.${key}`) : tag
  })

  const defaultTagsOptions = defaultTagsTranslated.map((tag) => ({ label: tag, value: tag }))
  const customTagsOptions = (customCodes ?? []).map((tag) => ({ label: tag.code, value: tag.code }))

  const handleSubmit = (value: AddPatientTagsForm) => {
    createOrUpdatePatient(new DecryptedPatient({ ...patient, ...getPreparedTagsToUpdatePatient(value.tags) }))
    form.resetFields()
  }

  const getInitialValues = (patient: DecryptedPatient) => {
    if (!patient) {
      return {}
    }

    const { tags } = getPatientDataFormated(patient, t, currentLanguage)
    return {
      tags: tags?.map((tag) => tag.code) ?? [],
    }
  }

  useEffect(() => {
    if (isPatientCreatedOrUpdateSuccessfully) {
      onClose()
    }
  }, [isPatientCreatedOrUpdateSuccessfully])

  const handleOnClose = () => {
    form.resetFields()
    onClose()
  }

  const getCustomFooter = () => {
    const getPopconfirmDescription = (codes: string[]) => {
      const isPlural = codes.length > 1
      const joinedCodes = codes.join(', ')
      const keyStart = isPlural ? 'modalPatientForm.popconfirm.saveLabels.start.other' : 'modalPatientForm.popconfirm.saveLabels.start.one'
      const keyEnd = isPlural ? 'modalPatientForm.popconfirm.saveLabels.end.other' : 'modalPatientForm.popconfirm.saveLabels.end.one'

      return (
        <p className="popconfirmDescription">
          {t(keyStart)} <span className="labelList">{joinedCodes}</span> {t(keyEnd)}
        </p>
      )
    }

    return [
      <Button key="back" onClick={handleOnClose}>
        {t('modalPatientForm.labels.cancel')}
      </Button>,

      newTagsToCreate.length !== 0 ? (
        <Popconfirm
          key="confirmClose"
          title={t('modalPatientForm.popconfirm.title')}
          description={getPopconfirmDescription(newTagsToCreate)}
          onConfirm={() => {
            createTags(
              newTagsToCreate.map(
                (tag) =>
                  new Code({
                    id: `RETINO_BRIDGE|${tag}|1`,
                    code: tag,
                    type: 'RETINO_BRIDGE',
                    version: '1',
                  }),
              ),
            )
            setNewTagsToCreate([])
            form.submit()
          }}
          onCancel={() => {
            form.submit()
            setNewTagsToCreate([])
          }}
          okText={t('common.yes')}
          cancelText={t('common.no')}
          placement="topRight"
        >
          <Button key="submit" type="primary">
            {t('modalPatientForm.labels.save')}
          </Button>
        </Popconfirm>
      ) : (
        <Button key="submit" type="primary" onClick={() => form.submit()}>
          {t('modalPatientForm.labels.save')}
        </Button>
      ),
    ]
  }

  return (
    <>
      <CustomModal isVisible={isVisible} handleClose={handleOnClose} title={t('modalAddPatientTag.title')} customFooter={getCustomFooter()}>
        <div className="modalPatientForm">
          {isPatientCreatingOrUpdatingLoading && <SpinLoader />}
          <Form
            className="modalPatientForm__form"
            layout="vertical"
            colon={false}
            form={form}
            onFinish={(values) => handleSubmit(values)}
            initialValues={patient ? getInitialValues(patient) : {}}
          >
            <div className="modalPatientForm__form__infoBlock">
              <div className="modalPatientForm__form__infoBlock__inputs">
                <Form.Item name="tags" label={t('modalAddPatientTag.label')}>
                  <Select
                    placeholder={t('modalPatientForm.labels.tags')}
                    size="large"
                    mode="tags"
                    options={[
                      {
                        label: t('patientInfo.tags.standard'),
                        options: defaultTagsOptions,
                      },
                      {
                        label: t('patientInfo.tags.custom'),
                        options: customTagsOptions,
                      },
                    ]}
                    onChange={(values) => {
                      const defaultTagsValues = defaultTagsOptions?.map((item) => item.value as string)
                      const customTagsValues = customTagsOptions?.map((item) => item.value)
                      const unknownTagsValues = values.filter((item: string) => !customTagsValues.includes(item)).filter((item: string) => !defaultTagsValues.includes(item))
                      setNewTagsToCreate(unknownTagsValues)
                    }}
                  />
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>
      </CustomModal>
    </>
  )
}
